import React from 'react';
import { Definition, Property } from '../../../data/Definition';
import { ChartProvider } from '../ChartProvider';
import { DwQuery, IDwFilter, IMeasure} from '../../../data/Query';
import ScatterConfigForm from './ScatterConfigForm';
import DataFrame from 'dataframe-js';
import DefaultChartLayout from '../DefPlotLayout';
import { getPlotDataProvider, IPlotProviderData } from '../../../data/dataProviders';
import { ScatterChartConfigManager } from './ScatterChartConfigManager';
import { ChartConfigManager } from '../ChartConfigManager';
import { scales } from '../../../colorscaleUtils';

export class ScatterChartProvider extends ChartProvider {

    constructor() {
        super('Scatter');
    }

    getQuery(definition: Definition, chartConfig: any): null | DwQuery {
        const configManager = new ScatterChartConfigManager(definition);

        if(!configManager.validate(chartConfig)) {
            return null;
        }

        return super.getQuery(definition, chartConfig);
    }

    configManager(definition: Definition) : ChartConfigManager {
        return new ScatterChartConfigManager(definition);
    }

    getConfigForm(definition: Definition, chartConfig: any, onSaveConfig: any) {
        return (
            <ScatterConfigForm definition={definition} chartConfig={chartConfig} saveConfig={onSaveConfig}></ScatterConfigForm>
        );
    }

    getPlotly(definition: Definition, chartConfig: any, data: any) {
        // use the default layout and update the items for the given chart type.
        // make a deep copy.
        //
        let layout: any = JSON.parse(JSON.stringify(DefaultChartLayout));

        if (!data || data.length === 0) {
            return {
                data: [],
                layout: layout
            };
        }

        // put the data into a data frame so we can standardize how
        // to interact with the rows.
        //
        const df = this.dataToDataFrame(data);

        // get the data provider for dimension.
        //
        const dataProvider = getPlotDataProvider(chartConfig.dimension, chartConfig.measure, df, chartConfig.color);

        const plotData = dataProvider.getData();

        //
        let traces: any[] = [];

        let trace1 = {
            mode: 'markers',
            type: 'scattergl',
            x: plotData.map((d: IPlotProviderData) => d.x),
            y: plotData.map((d: IPlotProviderData) => d.y),
            customdata: plotData.map((d: IPlotProviderData) => d.meta),
            marker: {}
        }

        if(chartConfig.color) {
            const rampName = chartConfig.colorStyle && chartConfig.colorStyle.scale ? chartConfig.colorStyle.scale : 'BlRdYlGrBu';
            const scale = scales[rampName].map((step:any) => {
                return step;
            });
        
            const _scale = chartConfig.colorStyle.inverse ? scale.reverse().map((step:any) => {
                return [1.0 - step[0], step[1]];
            }) : scale;

            console.log('scale', _scale, chartConfig.colorStyle);

            trace1.marker = {
                color: plotData.map((d: IPlotProviderData) => d.color),
                colorscale: _scale,
                colorbar: {
                    title: dataProvider.colorTitle(),
                    side: 'right',
                }
            }
        }

        traces.push(trace1);

        layout.xaxis.title = dataProvider.dimensionTitle();
        layout.yaxis.title = dataProvider.measureTitle();

        return {
            data: traces,
            layout: layout
        };
    }

    filterFromSelection(definition: Definition, chartConfig: any, selection: any): IDwFilter[] | null {
        const xMeasure: IMeasure = chartConfig.measure[0];
        const yMeasure: IMeasure = chartConfig.measure[1];

        const xProp = xMeasure.source.item as Property;
        const yProp = yMeasure.source.item as Property;

        return [
            {
                type: "property",
                id: this.getPath(definition, chartConfig.measure[0].source.item, chartConfig.measure[0].source.sub),
                name: xProp.property,
                operation: "between",
                value: selection.range.x
            },
            {
                type: "property",
                id: this.getPath(definition, chartConfig.measure[1].source.item, chartConfig.measure[1].source.sub),
                name: yProp.property,
                operation: "between",
                value: selection.range.y
            },
        ];
    }

    dataToDataFrame(data: any) {
        let rows: any[] = [];
        data.forEach((d: any) => {
            let row: any[] = [];

            Object.keys(d).forEach((key: any) => {
                row.push(d[key]);
            });

            rows.push(row);
        });

        let header: string[] = [];
        Object.keys(data[0]).forEach((key: any) => {
            header.push(key);
        });

        return new DataFrame(rows, header);
    }
}

